import React, { useState, useEffect, useRef } from 'react'
import { useDashMessages, useDashIntl } from '../../intl/intl.hooks'
import styled from '@emotion/styled'
import {
  colors,
  spacing,
  borderRadius,
  fontSize,
  fontWeight,
} from '@retailer-platform/shared-components/src/foundation'
import type { ConversationSession } from './hooks/useConversationHistory'

const messageIds = {
  title: 'retailerAgent.history.title' as const,
  loading: 'retailerAgent.history.loading' as const,
  empty: 'retailerAgent.history.empty' as const,
}

interface ConversationHistoryDropdownProps {
  sessions: ConversationSession[]
  isLoading: boolean
  currentTitle?: string
  onSelect: (sessionId: string) => void
}

const ControlRow = styled.div({
  display: 'flex',
  alignItems: 'center',
  gap: spacing.X8,
  position: 'relative',
  flex: 1,
  minWidth: 0,
})

const PillButton = styled.button({
  display: 'flex',
  alignItems: 'center',
  gap: spacing.X8,
  flex: 1,
  minWidth: 0,
  height: spacing.X32,
  padding: `0 ${spacing.X12}`,
  border: `1px solid ${colors.PRIMARY_TDS.DARK}`,
  borderRadius: '20px',
  background: colors.GRAYSCALE.X0,
  cursor: 'pointer',
  fontSize: fontSize.X14,
  fontWeight: fontWeight.MEDIUM,
  color: colors.GRAYSCALE.X80,
  overflow: 'hidden',
  whiteSpace: 'nowrap' as const,
  textOverflow: 'ellipsis',
})

const PillLabel = styled.span({
  flex: 1,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap' as const,
  textAlign: 'left' as const,
})

const Chevron = styled.span<{ isOpen: boolean }>(({ isOpen }) => ({
  display: 'inline-block',
  fontSize: fontSize.X12,
  transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
  transition: 'transform 150ms ease',
  color: colors.PRIMARY_TDS.DARK,
  flexShrink: 0,
}))

const DropdownContainer = styled.div({
  position: 'absolute',
  top: 'calc(100% + 4px)',
  left: 0,
  minWidth: '220px',
  maxWidth: '300px',
  background: colors.GRAYSCALE.X0,
  borderRadius: borderRadius.X8,
  boxShadow: '0 4px 16px rgba(0,0,0,0.14)',
  border: `1px solid ${colors.GRAYSCALE.X20}`,
  zIndex: 10,
  overflow: 'hidden',
})

const SessionList = styled.div({
  maxHeight: '240px',
  overflowY: 'auto',
})

const SessionItem = styled.button({
  display: 'flex',
  flexDirection: 'column',
  gap: spacing.X4,
  width: '100%',
  padding: `${spacing.X8} ${spacing.X12}`,
  border: 'none',
  background: 'none',
  cursor: 'pointer',
  textAlign: 'left' as const,
  ':hover': {
    background: colors.GRAYSCALE.X10,
  },
})

const SessionTitle = styled.span({
  fontWeight: fontWeight.SEMIBOLD,
  fontSize: fontSize.X14,
  color: colors.GRAYSCALE.X80,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap' as const,
  display: 'block',
})

const SessionTime = styled.span({
  fontSize: fontSize.X12,
  color: colors.GRAYSCALE.X50,
})

const LoadingRow = styled.div({
  padding: `${spacing.X8} ${spacing.X12}`,
  fontSize: fontSize.X14,
  color: colors.GRAYSCALE.X50,
})

const EmptyRow = styled.div({
  padding: `${spacing.X8} ${spacing.X12}`,
  fontSize: fontSize.X14,
  color: colors.GRAYSCALE.X50,
})

function useRelativeTime(isoString: string): string {
  const intl = useDashIntl()
  try {
    const diffMs = new Date(isoString).getTime() - Date.now()
    const diffSeconds = Math.round(diffMs / 1000)
    const diffMinutes = Math.round(diffSeconds / 60)
    const diffHours = Math.round(diffMinutes / 60)
    const diffDays = Math.round(diffHours / 24)

    if (Math.abs(diffDays) >= 1)
      return intl.formatRelativeTime(diffDays, 'day', { numeric: 'auto' })
    if (Math.abs(diffHours) >= 1)
      return intl.formatRelativeTime(diffHours, 'hour', { numeric: 'auto' })
    if (Math.abs(diffMinutes) >= 1)
      return intl.formatRelativeTime(diffMinutes, 'minute', { numeric: 'auto' })
    return intl.formatRelativeTime(diffSeconds, 'second', { numeric: 'auto' })
  } catch {
    return ''
  }
}

function SessionRow({ session, onClick }: { session: ConversationSession; onClick: () => void }) {
  const relativeTime = useRelativeTime(session.last_active_at)
  return (
    <SessionItem onClick={onClick}>
      <SessionTitle>{session.title}</SessionTitle>
      <SessionTime>{relativeTime}</SessionTime>
    </SessionItem>
  )
}

export function ConversationHistoryDropdown({
  sessions,
  isLoading,
  currentTitle,
  onSelect,
}: ConversationHistoryDropdownProps) {
  const messages = useDashMessages(messageIds)
  const [isOpen, setIsOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    function handleMouseDown(event: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }
    document.addEventListener('mousedown', handleMouseDown)
    return () => {
      document.removeEventListener('mousedown', handleMouseDown)
    }
  }, [])

  function handleSelect(sessionId: string) {
    onSelect(sessionId)
    setIsOpen(false)
  }

  const pillLabel = currentTitle ?? messages.title

  return (
    <ControlRow ref={containerRef}>
      <PillButton onClick={() => setIsOpen(prev => !prev)} aria-expanded={isOpen}>
        <PillLabel title={pillLabel}>{pillLabel}</PillLabel>
        <Chevron isOpen={isOpen}>▾</Chevron>
      </PillButton>
      {isOpen && (
        <DropdownContainer>
          <SessionList>
            {isLoading && <LoadingRow>{messages.loading}</LoadingRow>}
            {!isLoading && sessions.length === 0 && <EmptyRow>{messages.empty}</EmptyRow>}
            {!isLoading &&
              sessions.map(session => (
                <SessionRow
                  key={session.session_id}
                  session={session}
                  onClick={() => handleSelect(session.session_id)}
                />
              ))}
          </SessionList>
        </DropdownContainer>
      )}
    </ControlRow>
  )
}
